import {
  type EntityDef,
  type Level,
  type LevelMeta,
  LevelSchema,
  PLAYER_HEIGHT_TILES,
  PLAYER_WIDTH_TILES,
} from '@md/shared';
import type { Vec2 } from '../physics/aabb.js';
import type { CollisionGrid } from '../physics/world.js';
import {
  resolveTileLegend,
  TILE_OUT_OF_BOUNDS,
  type TileDef,
  type TileMarker,
} from './registry.js';

/** Erreur structurée renvoyée quand un niveau est invalide. */
export interface LevelIssue {
  readonly code: string;
  readonly message: string;
}

export class LevelParseError extends Error {
  readonly issues: readonly LevelIssue[];

  constructor(issues: readonly LevelIssue[]) {
    super(`Niveau invalide : ${issues.map((i) => `[${i.code}] ${i.message}`).join(' ; ')}`);
    this.name = 'LevelParseError';
    this.issues = issues;
  }
}

/** Niveau prêt à simuler : grille indexée, spawns et sorties résolus. */
export interface ParsedLevel extends CollisionGrid {
  readonly source: Level;
  readonly meta: LevelMeta;
  readonly width: number;
  readonly height: number;
  readonly tileSize: number;
  /** Grille aplatie, `width * height` définitions de tuiles. */
  readonly cells: readonly TileDef[];
  /** Coin haut-gauche de la hitbox au spawn, en tuiles, pour chaque joueur. */
  readonly spawns: readonly [Vec2, Vec2];
  /** Coordonnées entières de la tuile de sortie de chaque joueur. */
  readonly exits: readonly [Vec2, Vec2];
  readonly entities: readonly EntityDef[];
  tileAt(tx: number, ty: number): TileDef;
}

export interface ParseLevelOptions {
  /** Légende du GameModule, fusionnée avec les tuiles de base. */
  readonly tileLegend?: Readonly<Record<string, TileDef>>;
  /** Taille de la hitbox des joueurs, en tuiles. */
  readonly playerSize?: { readonly w: number; readonly h: number };
}

const MARKER_ORDER: readonly TileMarker[] = ['spawn-a', 'spawn-b', 'exit-a', 'exit-b'];

/**
 * Valide (zod) puis analyse un niveau JSON.
 *
 * Contrôles effectués ici — le strict nécessaire pour qu'un niveau soit
 * simulable ; le lint complet et le solveur de finissabilité arriveront en
 * phase 4 (spec §8) :
 *  1. schéma zod ;
 *  2. dimensions ASCII cohérentes avec `grid` ;
 *  3. tous les caractères ∈ base ∪ légende du module ;
 *  4. exactement un `P`, un `M`, un `p`, un `m` ;
 *  5. aucun spawn ni sortie à l'intérieur d'un mur.
 */
export function parseLevel(input: unknown, options: ParseLevelOptions = {}): ParsedLevel {
  const parsed = LevelSchema.safeParse(input);
  if (!parsed.success) {
    throw new LevelParseError(
      parsed.error.issues.map((issue) => ({
        code: 'SCHEMA',
        message: `${issue.path.join('.') || '(racine)'} : ${issue.message}`,
      })),
    );
  }

  const level = parsed.data;
  const legend = resolveTileLegend(options.tileLegend);
  const { width, height, tileSize } = level.grid;
  const issues: LevelIssue[] = [];

  if (level.tiles.length !== height) {
    issues.push({
      code: 'GRID_HEIGHT',
      message: `grid.height = ${height} mais la carte compte ${level.tiles.length} ligne(s).`,
    });
  }

  const cells: TileDef[] = new Array<TileDef>(width * height).fill(
    legend['.'] ?? TILE_OUT_OF_BOUNDS,
  );
  const markers = new Map<TileMarker, Vec2[]>(MARKER_ORDER.map((m) => [m, []]));

  for (let ty = 0; ty < Math.min(height, level.tiles.length); ty += 1) {
    const row = level.tiles[ty] ?? '';
    if (row.length !== width) {
      issues.push({
        code: 'GRID_WIDTH',
        message: `ligne ${ty} : ${row.length} caractère(s) pour grid.width = ${width}.`,
      });
    }
    for (let tx = 0; tx < Math.min(width, row.length); tx += 1) {
      const char = row[tx] ?? '.';
      const def = legend[char];
      if (!def) {
        issues.push({
          code: 'UNKNOWN_TILE',
          message: `caractère "${char}" inconnu en (${tx}, ${ty}) — absent de la base et de la légende du module.`,
        });
        continue;
      }
      cells[ty * width + tx] = def;
      if (def.marker) {
        markers.get(def.marker)?.push({ x: tx, y: ty });
      }
    }
  }

  for (const marker of MARKER_ORDER) {
    const found = markers.get(marker) ?? [];
    if (found.length !== 1) {
      issues.push({
        code: 'MARKER_COUNT',
        message: `le marqueur "${marker}" doit apparaître exactement 1 fois (trouvé ${found.length}).`,
      });
    }
  }

  if (issues.length > 0) {
    throw new LevelParseError(issues);
  }

  const spawnA = markers.get('spawn-a')?.[0];
  const spawnB = markers.get('spawn-b')?.[0];
  const exitA = markers.get('exit-a')?.[0];
  const exitB = markers.get('exit-b')?.[0];
  /* c8 ignore next 3 -- garanti par le contrôle MARKER_COUNT ci-dessus */
  if (!spawnA || !spawnB || !exitA || !exitB) {
    throw new LevelParseError([{ code: 'MARKER_COUNT', message: 'marqueurs manquants.' }]);
  }

  const playerW = options.playerSize?.w ?? PLAYER_WIDTH_TILES;
  const playerH = options.playerSize?.h ?? PLAYER_HEIGHT_TILES;

  const tileAt = (tx: number, ty: number): TileDef => {
    if (tx < 0 || ty < 0 || tx >= width || ty >= height) {
      return TILE_OUT_OF_BOUNDS;
    }
    return cells[ty * width + tx] ?? TILE_OUT_OF_BOUNDS;
  };

  for (const [name, cell] of [
    ['spawn-a', spawnA],
    ['spawn-b', spawnB],
    ['exit-a', exitA],
    ['exit-b', exitB],
  ] as const) {
    if (tileAt(cell.x, cell.y).solid) {
      issues.push({
        code: 'MARKER_IN_WALL',
        message: `"${name}" est posé dans un mur en (${cell.x}, ${cell.y}).`,
      });
    }
  }

  if (issues.length > 0) {
    throw new LevelParseError(issues);
  }

  // Le robot est centré horizontalement dans sa tuile de spawn et posé sur son
  // bord bas : les deux marges valent (1 - taille) et (1 - hauteur).
  const spawnPoint = (cell: Vec2): Vec2 => ({
    x: cell.x + (1 - playerW) / 2,
    y: cell.y + (1 - playerH),
  });

  return {
    source: level,
    meta: level.meta,
    width,
    height,
    tileSize,
    cells,
    spawns: [spawnPoint(spawnA), spawnPoint(spawnB)],
    exits: [exitA, exitB],
    entities: level.entities,
    tileAt,
    isSolidAt: (tx, ty) => tileAt(tx, ty).solid,
    isOneWayAt: (tx, ty) => tileAt(tx, ty).oneWay,
  };
}
